import { useQuery } from "@tanstack/react-query";
import { dashboardService } from "../services/api/dashboardService";
import PageContainer from "../components/layout/PageContainer";
import DashboardErrorBanner from "../components/dashboard/DashboardErrorBanner";
import DashboardStatsCards from "../components/dashboard/DashboardStatsCards";
import ComplianceTrendsChart from "../components/dashboard/ComplianceTrendsChart";
import QuickOverview from "../components/dashboard/QuickOverview";
import IssuesBySeverity from "../components/dashboard/IssuesBySeverity";
import RecentActivity from "../components/dashboard/RecentActivity";
import { transformActivityItem } from "../utils/dashboardHelpers";

const Dashboard = () => {
  // Fetch Dashboard Overview
  const {
    data: overviewData,
    isLoading: isLoadingOverview,
    isError: isErrorOverview,
    error: overviewError,
  } = useQuery({
    queryKey: ["dashboard-overview"],
    queryFn: () => dashboardService.getOverview(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
  });

  // Fetch Recent Activity
  const {
    data: recentActivityData,
    isLoading: isLoadingActivity,
    isError: isErrorActivity,
    error: activityError,
  } = useQuery({
    queryKey: ["dashboard-recent-activity"],
    queryFn: () => dashboardService.getRecentActivity(10),
    staleTime: 2 * 60 * 1000, // 2 minutes
    retry: 2,
  });

  // Fetch Compliance Statistics
  const {
    data: complianceStatsData,
    isLoading: isLoadingCompliance,
    isError: isErrorCompliance,
    error: complianceError,
  } = useQuery({
    queryKey: ["dashboard-compliance-stats"],
    queryFn: () => dashboardService.getComplianceStats(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
  });

  // Fetch Issues by Severity
  const {
    data: issuesBySeverityData,
    isLoading: isLoadingIssues,
    isError: isErrorIssues,
    error: issuesError,
  } = useQuery({
    queryKey: ["dashboard-issues-by-severity"],
    queryFn: () => dashboardService.getIssuesBySeverity(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
  });

  // Extract data from API response (handle both direct data and wrapped in success/data)
  const overviewDataExtracted = overviewData?.data || overviewData;
  const complianceStatsExtracted =
    complianceStatsData?.data || complianceStatsData;
  const recentActivityExtracted =
    recentActivityData?.data || recentActivityData;
  const issuesBySeverityExtracted =
    issuesBySeverityData?.data || issuesBySeverityData;

  // Transform recent activity data
  const recentActivity = Array.isArray(recentActivityExtracted)
    ? recentActivityExtracted.map(transformActivityItem)
    : recentActivityExtracted?.activities
      ? recentActivityExtracted.activities.map(transformActivityItem)
      : [];

  // Error state
  const hasError =
    isErrorOverview || isErrorActivity || isErrorCompliance || isErrorIssues;

  return (
    <PageContainer
      title="Dashboard Overview"
      subtitle="Monitor your accessibility compliance and recent activity"
    >
      {/* Error Banner */}
      {hasError && (
        <DashboardErrorBanner
          errors={{
            overviewError,
            activityError,
            complianceError,
            issuesError,
          }}
        />
      )}

      {/* Stat Cards */}
      <DashboardStatsCards
        overviewData={overviewDataExtracted}
        isLoading={isLoadingOverview}
      />

      {/* Compliance Trends and Quick Overview */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 md:gap-6 mb-6">
        {/* <ComplianceTrendsChart
          data={complianceStatsExtracted}
          isLoading={isLoadingCompliance}
          isError={isErrorCompliance}
        /> */}
        {/* Issues by Severity */}
        <IssuesBySeverity
          data={issuesBySeverityExtracted}
          isLoading={isLoadingIssues}
          isError={isErrorIssues}
        />
        <QuickOverview
          overviewData={overviewDataExtracted}
          complianceStats={complianceStatsExtracted}
          isLoading={isLoadingCompliance || isLoadingOverview}
        />
      </div>

      {/* Recent Activity */}
      <RecentActivity
        activities={recentActivity}
        isLoading={isLoadingActivity}
        isError={isErrorActivity}
      />
    </PageContainer>
  );
};

export default Dashboard;
